/**
 * OUT OF CREDITS CONTEXT
 *
 * Provides a global way to trigger the OutOfCreditsModal from any component.
 * Wrap your app with <OutOfCreditsProvider> and call showOutOfCredits() when
 * a credit limit is hit.
 *
 * Usage:
 *   const { showOutOfCredits } = useOutOfCredits()
 *   showOutOfCredits({ creditType: 'sms', used: 250, limit: 250, packRemaining: 0, planTier: 'paid' })
 */

import { createContext, useContext, useState, useCallback } from 'react'
import OutOfCreditsModal from '../components/OutOfCreditsModal'

const OutOfCreditsContext = createContext(null)

export const useOutOfCredits = () => {
  const ctx = useContext(OutOfCreditsContext)
  if (!ctx) {
    throw new Error('useOutOfCredits must be used within an OutOfCreditsProvider')
  }
  return ctx
}

export const OutOfCreditsProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    creditType: 'sms',
    used: 0,
    limit: 0,
    packRemaining: 0,
    planTier: 'free',
  })

  const showOutOfCredits = useCallback(({ creditType, used, limit, packRemaining = 0, planTier = 'free' }) => {
    setModalState({
      isOpen: true,
      creditType,
      used,
      limit,
      packRemaining,
      planTier,
    })
  }, [])

  const hideOutOfCredits = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
  }, [])

  return (
    <OutOfCreditsContext.Provider value={{ showOutOfCredits, hideOutOfCredits }}>
      {children}
      <OutOfCreditsModal
        isOpen={modalState.isOpen}
        onClose={hideOutOfCredits}
        creditType={modalState.creditType}
        used={modalState.used}
        limit={modalState.limit}
        packRemaining={modalState.packRemaining}
        planTier={modalState.planTier}
      />
    </OutOfCreditsContext.Provider>
  )
}

export default OutOfCreditsProvider
